import { useState } from 'react';
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDebounce } from '@odo/shared-ui';
import {
    useSendSparkAccountRequest,
    useSparkAccountRequestPreview
} from '../../../../hooks/usePracticeDetail';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { UDField } from '../../users/UDField';
import { UDInput } from '../../users/UDInput';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

interface RequestSparkAccountModalProps {
    practiceId: string;
    onClose: () => void;
}

// How long to hold off refetching the preview after the user stops typing in
// one of the three override inputs - long enough to skip a request per
// keystroke, short enough that the preview still reads as "live".
const PREVIEW_DEBOUNCE_MS = 400;

// Sibling to CreateBillComCustomerModal (same overlay/card chrome), wider to
// fit the email preview alongside the three optional inputs. Unlike that
// modal, submitting here never writes to this practice - the email is
// composed entirely server-side from the practice's saved details plus these
// overrides (see spark-account.service.ts), so there's no dirty-field/save
// interplay to guard against, only the muted note below telling the user
// which source of truth the email actually reads from.
export function RequestSparkAccountModal({ practiceId, onClose }: RequestSparkAccountModalProps) {
    const { showToast } = useToast();
    const [requestedUsername, setRequestedUsername] = useState('');
    const [doctorType, setDoctorType] = useState('');
    const [website, setWebsite] = useState('');

    const overrides = { requestedUsername, doctorType, website };
    const debouncedOverrides = useDebounce(overrides, PREVIEW_DEBOUNCE_MS);

    const preview = useSparkAccountRequestPreview(practiceId, debouncedOverrides);
    const sendRequest = useSendSparkAccountRequest(practiceId);

    useEscapeKey(onClose, !sendRequest.isPending);

    const handleSubmit = async () => {
        try {
            const result = await sendRequest.mutateAsync(overrides);
            showToast(`Spark account request sent to ${result.recipients.join(', ')}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to send Spark account request'), 'error');
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-spark-account-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[960px] max-h-[92vh] flex flex-col overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start gap-3.5 shrink-0">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
                        <PaperAirplaneIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2
                            id="request-spark-account-title"
                            className="text-[17px] font-bold text-gray-900"
                        >
                            Request Spark account
                        </h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed mt-1">
                            Emails the Spark account team to request a new account for this
                            practice.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={sendRequest.isPending}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 pb-5 flex flex-col gap-4 overflow-y-auto">
                    <div className="grid grid-cols-3 gap-3">
                        <UDField label="Requested username *">
                            <UDInput
                                value={requestedUsername}
                                onChange={setRequestedUsername}
                                placeholder="Required"
                                autoFocus
                            />
                        </UDField>
                        <UDField label="Doctor type">
                            <UDInput
                                value={doctorType}
                                onChange={setDoctorType}
                                placeholder="Optional"
                            />
                        </UDField>
                        <UDField label="Website">
                            <UDInput value={website} onChange={setWebsite} placeholder="Optional" />
                        </UDField>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[12px] font-semibold text-gray-500">
                            Email preview
                        </span>
                        {preview.data && (
                            <p className="text-[12px] text-gray-500">
                                To: {preview.data.recipients.join(', ')}
                            </p>
                        )}
                        <div className="border border-gray-200 rounded-lg overflow-hidden h-[58vh] bg-gray-50">
                            {preview.isLoading && (
                                <div className="w-full h-full flex items-center justify-center">
                                    <span className="h-4 w-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                                </div>
                            )}
                            {preview.isError && (
                                <div className="w-full h-full flex items-center justify-center px-4 text-center">
                                    <span className="text-[13px] text-red-600">
                                        {apiErrorMessage(
                                            preview.error,
                                            'Failed to load the email preview'
                                        )}
                                    </span>
                                </div>
                            )}
                            {preview.data && (
                                <iframe
                                    title="Spark account request email preview"
                                    srcDoc={preview.data.html}
                                    sandbox=""
                                    className="w-full h-full bg-white"
                                />
                            )}
                        </div>
                    </div>

                    <p className="text-[11px] text-gray-400">
                        This email is composed from this practice&apos;s saved details - if you have
                        unsaved edits above, save them first so the email matches.
                    </p>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={sendRequest.isPending}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={
                            sendRequest.isPending ||
                            !preview.data ||
                            requestedUsername.trim().length === 0
                        }
                        onClick={() => void handleSubmit()}
                        className="bg-black text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {sendRequest.isPending && (
                            <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        )}
                        {sendRequest.isPending ? 'Sending…' : 'Send request'}
                    </button>
                </div>
            </div>
        </div>
    );
}
